import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "~/db/db";
import { useGetClienteShare } from "~hooks/useSincronizacao";
import { clienteShareMock } from "../mocks/new/ClienteShare.mock";
import useVendedor from "~fetchs/useVendedor";
import { mesParaData, nomeProdutoLimpo } from "../utils/formatters";
import { getPlayerColor } from "../../PriceSearchV2/utils/playerColors";
import { usarMock } from "../utils/dataSource";


const coresPlayersGV = {
  "QUARTZOLIT":      "#034EA2",
  "HIDRA":           "#4299E1",
  "INKOR":           "#1ABC9C",
  "SILAQUA":         "#27AE60",
  "HIPERMASSA":      "#9B59B6",
  "CERAMFIX":        "#85C1E9",
  "AXTON":           "#F39C12",
  "CIMENTOLIT":      "#76D7C4",
  "KERACOLL":        "#2ECC71",
  "ARGAMASSA REAL":  "#C0392B",
  "EUROFLEX":        "#8E44AD",
  "ZL":              "#2C3E50",
  "VIAPOL":          "#D35400",
  "SIKA":            "#2980B9",
  "VEDACIT (OTTO)":  "#16A085",
  "ITABRANCA":       "#F1948A",
  "ITAMASSA":        "#A9CCE3",
  "HIPERCOLA":       "#E74C3C",
  "ARGAFACIL":       "#3498DB",
  "CERTA":           "#BDC3C7",
  "TECNOCOLA":       "#117864",
  "ARGAPOLI":        "#6C3483",
  "LIGAFIX":         "#7F8C8D",
};

const emptyDados = { clientesShare: [], winLosses: [], micros: [], createdAt: null };

const mesmaCompetencia = (anoMes, data) => {
  if (!anoMes || !data) return false;
  const d = new Date(anoMes);
  return d.getFullYear() === data.getFullYear() && d.getMonth() === data.getMonth();
};

const formatarMesLabel = (anoMes) => {
  const date = new Date(anoMes);
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};

const flattenVendedorClienteShare = (rawArr, vendedorSelecionado) => {
  if (!Array.isArray(rawArr) || !rawArr.length) return rawArr;
  if (!rawArr.some((v) => Array.isArray(v?.basesClienteShare))) return rawArr;

  const filtered = vendedorSelecionado != null
    ? rawArr.filter((v) => v?.id === vendedorSelecionado)
    : rawArr;

  const all = filtered.flatMap((v) => v?.basesClienteShare ?? []);
  if (filtered.length <= 1) return all;

  const byId = new Map();
  for (const item of all) {
    if (!byId.has(item.id)) byId.set(item.id, []);
    byId.get(item.id).push(item);
  }

  return [
    { id: 1, clientesShare: (byId.get(1) ?? []).flatMap((i) => i.clientesShare ?? []) },
    { id: 2, winLosses:     (byId.get(2) ?? []).flatMap((i) => i.winLosses     ?? []) },
    { id: 3, micros:        (byId.get(3) ?? []).flatMap((i) => i.micros        ?? []) },
    { id: 4, createdAt:     (byId.get(4) ?? []).map((i) => i.createdAt).filter(Boolean).sort().pop() ?? null },
  ];
};

const extrairDados = (source) => {
  const dataArr = Array.isArray(source) && source.every((d) => typeof d?.id === 'number')
    ? source
    : source?.[0]?.[0]?.data?.[0] ?? null;

  if (!dataArr) return emptyDados;

  return {
    clientesShare: dataArr.find((d) => d.id === 1)?.clientesShare ?? [],
    winLosses:     dataArr.find((d) => d.id === 2)?.winLosses     ?? [],
    micros:        dataArr.find((d) => d.id === 3)?.micros        ?? [],
    createdAt:     dataArr.find((d) => d.id === 4)?.createdAt     ?? null,
  };
};

const extractItemsArray = (response) => {
  const dataArr =
    response?.data?.data?.[0] ??
    response?.data?.[0]      ??
    response?.[0]            ??
    null;
  return Array.isArray(dataArr) ? dataArr : null;
};

const buildShareLookup = (anosArr) => {
  const byPlayerMes = new Map();
  const byMes = new Map();
  for (const entry of anosArr) {
    const consumo = Number(entry.consumo) || 0;
    if (!consumo) continue;
    const playerKey = `${entry.player}|${entry.anoMes}`;
    byPlayerMes.set(playerKey, (byPlayerMes.get(playerKey) ?? 0) + consumo);
    byMes.set(entry.anoMes, (byMes.get(entry.anoMes) ?? 0) + consumo);
  }
  return { byPlayerMes, byMes };
};

const buildShareDatasets = (uniquePlayers, uniqueMonths, { byPlayerMes, byMes }) =>
  uniquePlayers.map((player, idx) => {
    const color = getPlayerColor(player, idx, coresPlayersGV);
    const data = uniqueMonths.map((monthKey) => {
      const total = byMes.get(monthKey) ?? 0;
      if (!total) return 0;
      const playerSum = byPlayerMes.get(`${player}|${monthKey}`) ?? 0;
      return Math.round((playerSum / total) * 10000) / 100;
    });
    return { label: player, data, backgroundColor: color, stack: "stack" };
  });

export const useClienteShareWinLossData = ({ grupoProduto, produto, micro, mes } = {}) => {
  const { vendedorSelecionado } = useVendedor();
  const [rawItems, setRawItems] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasMock = usarMock(clienteShareMock);

  const { data: queryData, isFetching, isRefetching, isError } =
    useGetClienteShare(!hasMock && navigator.onLine, 0, 0, null);

  const apiItems = useMemo(() => {
    if (hasMock) return clienteShareMock?.[0]?.[0]?.data?.[0] ?? null;
    return extractItemsArray(queryData);
  }, [hasMock, queryData]);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.clienteShare.toArray();
      const stored = rows?.[0]?.payload;
      if (stored) setRawItems(stored);
    } catch (err) {
      console.error("Erro ao carregar clienteShare do Dexie:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasMock) {
      setRawItems(clienteShareMock?.[0]?.[0]?.data?.[0] ?? null);
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevVendedor = useRef(undefined);
  useEffect(() => {
    if (vendedorSelecionado === undefined) return;
    if (prevVendedor.current === undefined) {
      prevVendedor.current = vendedorSelecionado;
      return;
    }
    if (prevVendedor.current === vendedorSelecionado) return;
    prevVendedor.current = vendedorSelecionado;
    if (!hasMock) {
      setRawItems(null);
      loadFromDexie();
    }
  }, [vendedorSelecionado, hasMock, loadFromDexie]);

  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    setIsRefreshing(false);

    if (!isError && apiItems != null) {
      setRawItems(apiItems);
      setIsLoading(false);
      db.clienteShare.clear()
        .then(() => db.clienteShare.add({ payload: apiItems, syncedAt: new Date().toISOString() }))
        .catch((err) => console.error("Erro ao salvar clienteShare no Dexie:", err));
    }
  }, [apiItems, hasMock, isError, isFetching, isRefetching]);

  useEffect(() => {
    const handleOffline = () => {
      if (!hasMock) {
        setIsRefreshing(false);
        loadFromDexie();
      }
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, [hasMock, loadFromDexie]);

  const normalizedItems = useMemo(
    () => flattenVendedorClienteShare(rawItems, vendedorSelecionado),
    [rawItems, vendedorSelecionado]
  );

  return useMemo(() => {
    const { clientesShare, winLosses, micros, createdAt } = extrairDados(normalizedItems);

    const grupoFiltro =
      grupoProduto && grupoProduto !== "todos" ? String(grupoProduto).toUpperCase() : null;

    // Produto = PRODHIR 5 (antigo SKU). Backend passou a enviar `produto` no payload.
    const produtoFiltro =
      produto && produto !== "todos" ? nomeProdutoLimpo(produto).toUpperCase() : null;

    const microFiltro = micro ? Number(micro) : null;

    const flatAnos = clientesShare.flatMap((cs) =>
      Array.isArray(cs?.anos)
        ? cs.anos.map((a) => ({
            ...a,
            grupoProduto: a.grupoProduto ?? cs.grupoProduto,
            produto: a.produto ?? cs.produto,
            microrregiaoId: a.microrregiaoId ?? cs.microrregiaoId,
          }))
        : [cs]
    );

    const allAnos = flatAnos.filter(
      (a) =>
        a &&
        a.anoMes &&
        a.player != null &&
        (!grupoFiltro || String(a.grupoProduto ?? "").toUpperCase() === grupoFiltro) &&
        (!produtoFiltro || nomeProdutoLimpo(a.produto).toUpperCase() === produtoFiltro) &&
        (microFiltro == null || Number(a.microrregiaoId) === microFiltro)
    );



    const limite = mesParaData(mes);
    const dentroDoLimite = (anoMes) => {
      if (!limite) return true;
      const d = new Date(anoMes);
      return d.getFullYear() * 12 + d.getMonth() <= limite.getFullYear() * 12 + limite.getMonth();
    };

    const uniqueMonths = [...new Set(allAnos.map((a) => a.anoMes))]
      .filter(dentroDoLimite)
      .sort()
      .slice(-4);
    const labels = uniqueMonths.map(formatarMesLabel);

    const uniquePlayers = [...new Set(allAnos.map((a) => a.player))];
    const shareLookup = buildShareLookup(allAnos.filter((a) => uniqueMonths.includes(a.anoMes)));
    const datasets = buildShareDatasets(uniquePlayers, uniqueMonths, shareLookup);

    const clientShareChartData = labels.length > 0 ? { labels, datasets } : null;

    const mapCliente = (item) => ({
      cliente: item.razaoSocial?.trim() || item.codigoCliente || String(item.clienteId),
      codigo: item.codigoCliente ?? null,
      clienteId: item.clienteId ?? null,
      valor: item.consumo,
    });


    const referencias = [...winLosses].sort((a, b) =>
      String(a?.dataReferencia ?? "").localeCompare(String(b?.dataReferencia ?? "")),
    );
    const doMes = limite
      ? referencias.find((w) => mesmaCompetencia(w?.dataReferencia, limite))
      : referencias[referencias.length - 1];

    const aumentaram = (doMes?.wins ?? []).slice(0, 7).map(mapCliente);
    const diminuiram = (doMes?.loss ?? []).slice(0, 7).map(mapCliente);

    return { clientShareChartData, diminuiram, aumentaram, micros, createdAt, isLoading, isRefreshing };
  }, [normalizedItems, grupoProduto, produto, micro, mes, isLoading, isRefreshing]);
};
